import {
  getExtraFeesOptions,
  searchCustomerOptions,
  getFeeOptions,
  deleteStorageFees,
  getFeeLogger,
  getFeeCharge
} from "@/api/order/firstOrder";
import moment from "moment";

/**
 * 表格字段与搜索栏配置
 *
 * 返回结构：
 *   columns    —— iView Table 列定义
 *   headers    —— 搜索栏 DSL（自研，非 cHeader）
 *   fullfilled —— 下拉数据填充：一个 loader 一次取数，返回 { [key]: options } 映射
 *
 * headers 支持的 type：
 *   inputFuzzy     文本模糊搜索              form[key] 为字符串
 *                  可传 textarea: true 使用多行文本域，rows 指定行数
 *   dateTypeRange  时间类型下拉 + 日期范围    form[key] 为 { type, range }
 *   autocomplete   远程搜索（如客户账号）     form[key] 为选中值，另存 form[key + 'Item']
 *                  需提供 queryCallback(query) -> Promise<options[]>，组件内做防抖
 *   select         普通下拉                  form[key] 为选中值
 *                  可传 filterable（输入过滤）、clearable（可清空）
 *
 * 所有 type 均支持可选 default：默认值，可传字面量或函数 (item, vm) => 值。
 * 在「初始化」与点「重置」时回填；非空默认值会自动作为参数参与请求。
 *   dateTypeRange 的 default 可传字符串（直接指定 type）或 { type, range }
 *   autocomplete  可用 defaultLabel 指定输入框回显文案
 *
 * buttons 支持的形态：
 *   单个按钮：
 *     { key, label, type, icon, action }
 *   下拉按钮（list 为菜单项）：
 *     { key, label, type, icon, list: [{ key, label, action }] }
 *   action 为触发的事件名（由 SimpleTable 统一 $emit('button-click', action, btn)）
 *
 * modals 配置：{ [key]: { width, height, component, open } }
 *   width / height  仅配置大小（数字为 px，或字符串如 '60%'）
 *   component       模态框内部渲染的组件（可传组件对象或异步 import 的函数）
 *   open({ setParams, close, setTitle, refresh }, payload)  打开回调：
 *       setParams(params)  向内部组件注入 params（只放业务数据）
 *       close()            关闭模态框
 *       setTitle(title)    设置模态框标题
 *       refresh()          重新拉取表格数据（open 回调里可用）
 *       payload            调用方数据；操作列通过 vm.openModal(key, { row, index, category }) 传入
 *   内部组件会收到 props：params / close / setTitle / refresh
 *   注：refresh 和 close 一样由 SimpleTable 直接透传，不要塞进 setParams
 */

/**
 * 组装「开始报价 / 查看报价」弹窗的参数
 *
 * 在 setParams 之前调用：请求报价明细接口，直接把 payload 交给 setParams。
 * 后端严格按弹窗需要的字段返回（小驼峰），这里不做字段映射；
 * 弹窗组件只按 params 渲染，自己不做初始化。
 *
 * @returns {Promise<Object>} 供 setParams 展开的参数对象
 */
function buildChargeFeeParams(row = {}) {
  // 明细接口：入参为当前行 id（后端参数名如不同，改这里）
  return getFeeCharge({ id: row.id }).then(res => {
    // 业务失败（res.data.code 有值）：抛出后端的提示，由调用方统一提示
    if (res.data.code) {
      return Promise.reject(new Error(res.data.msg || "获取报价明细失败"));
    }
    // 直接透传后端 payload
    return res.data.payload;
  });
}

export default function getFields(category, vm) {
  if (category === 'storageFees') {
    return {
      // ============ 表格列 ============
      columns: [
        {type: 'index', title: '序号', width: 70, align: 'center', fixed: 'left'},
        {type: 'selection', width: 60, align: 'center', fixed: 'left'},
        {title: '订单号', key: 'order_number', width: 140, align: 'center', tooltip: true, fixed: 'left'},
        {title: '预约号', key: 'appointment_number', width: 140, align: 'center', tooltip: true, fixed: 'left'},
        {title: '送仓类型', key: 'warehouse_type', width: 110, align: 'center', tooltip: true, fixed: 'left'},
        {title: '客户账号', key: 'customer_account', width: 120, align: 'center', tooltip: true, fixed: 'left'},
        {title: '服务仓库', key: 'service_warehouse', width: 120, align: 'center', tooltip: true},
        {title: '送仓仓库', key: 'delivery_warehouse', width: 120, align: 'center', tooltip: true},
        {title: '入仓时间', key: 'inbound_time', width: 180, align: 'center', tooltip: true},
        {title: '送仓时间', key: 'delivery_time', width: 180, align: 'center', tooltip: true},
        {title: '仓储时长', key: 'storage_duration', width: 100, align: 'center', tooltip: true},
        {title: '计费单位', key: 'billing_unit', width: 100, align: 'center', tooltip: true},
        {title: '计费单价', key: 'billing_price', width: 100, align: 'center', tooltip: true},
        {title: '送仓数量', key: 'delivery_quantity', width: 100, align: 'center', tooltip: true},
        {title: '重量', key: 'weight', width: 100, align: 'center', tooltip: true},
        {title: '体积', key: 'volume', width: 100, align: 'center', tooltip: true},
        {title: '计费金额', key: 'billing_amount', width: 100, align: 'center', tooltip: true},
        {title: '币种', key: 'currency', width: 100, align: 'center', tooltip: true},
        {title: '创建时间', key: 'created_at', width: 180, align: 'center', tooltip: true},
        {title: '创建渠道', key: 'created_channel', width: 160, align: 'center', tooltip: true},
        {title: '关联账单', key: 'related_bill', width: 140, align: 'center', tooltip: true},
        {title: '销售人员', key: 'sales_name', width: 120, align: 'center', tooltip: true},
        {
          title: '订单状态',
          key: 'status',
          width: 100,
          align: 'center',
          render: (h, params) => {
            if (!params.row.is_deleted) {
              // 组件名用字符串即可：Tag/Tooltip 已由 main.js 的 Vue.use(ViewUI) 全局注册
              // （项目用的是 view-design = iView 4，不要从 "iview" 包引入）
              return h('Tag', {props: {color: 'success'}}, '正常');
            }
            // 已删除：用 Tooltip 包住 Tag，悬浮展示删除信息
            // transfer: true —— 浮层挂到 body 上，否则会被表格容器的 overflow: hidden 裁掉
            return h('Tooltip', {
              props: {content: params.row.is_deleted, transfer: true}
            }, [
              h('Tag', {props: {color: 'error'}}, '已删除')
            ]);
          }
        },
        {title: '操作人员', key: 'operator_name', width: 130, align: 'center', tooltip: true},

        {
          title: '操作',
          key: 'action',
          width: 190,
          align: 'center',
          fixed: 'right',
          render: (h, params) => {
            if (params.row.counldNotDelete === false) {
              return h('div', [
                h('Button', {
                  props: {type: 'error', size: 'small'},
                  style: {marginRight: '5px'},
                  on: {
                    click: () => {
                      vm.$Modal.confirm({
                        title: '提示',
                        content: `确认删除订单${params.row.order_number}的仓租费用么？`,
                        // loading: true -> 点「确定」后按钮进入 loading 且弹窗不自动关闭
                        // （iView 内部：loading 为真时只把 buttonLoading 置 true，不关弹窗）
                        loading: true,
                        onOk: async () => {
                          try {
                            const res = await deleteStorageFees({ id: params.row.id });
                            if (res.data.code) {
                              vm.$Message.error(res.data.msg);
                            } else {
                              vm.$Message.success('删除成功');
                              // 刷新列表（vm 即 SimpleTable 实例）
                              vm.refresh();
                            }
                          } catch (e) {
                            vm.$Message.error((e && e.message) || '删除失败');
                          } finally {
                            // 请求结束后手动关闭确认框（loading: true 时它不会自己关）
                            vm.$Modal.remove();
                          }
                        }
                      });
                    }
                  }
                }, '删除'),
                h('Button', {
                  props: {type: 'primary', size: 'small'},
                  style: {marginLeft: '5px'},
                  on: {
                    click: () => {
                      // 打开 modals 里名为 log 的弹窗，并把当前行作为 payload 传进去
                      // payload 会作为第 2 个参数交给 modals.log.open(ctx, payload)
                      if (vm && vm.openModal) {
                        vm.openModal('log', {
                          row: params.row,
                          index: params.index,
                          category
                        });
                      }
                    }
                  }
                }, '日志')
              ]);
            }
          }
        }
      ],

      // ============ 搜索栏 DSL ============
      // 说明：每个搜索项的 key 都会在 refresh 时作为参数名附加到请求参数中
      headers: [
        {
          type: 'inputFuzzy',
          key: 'shipmentNumber',
          label: '订单号',
          placeholder: '请输入订单号',
          // 多行文本域：开启后一次可输入多个订单号（每行一个）
          textarea: true,
          rows: 1
        },
        {
          // 一个类型下拉 + 一个日期范围，四个时间字段共用这一组控件
          // 请求参数：按选中类型拆成 [type]Start / [type]End（如 order_create_timeStart / order_create_timeEnd）
          type: 'dateTypeRange',
          key: 'dateRange',
          label: '时间',
          typeOptions: [
            {label: '杂费创建时间', value: 'createTime'},
            {label: '送仓时间', value: 'sendTime'},
            {label: '入仓时间', value: 'inboundTime'}
          ]
        },
        {
          // 请求参数：clientId = 选中的客户 id
          // 选项默认按 { value, label } 取值；如字段不同可传 config: { label, value }
          type: 'autocomplete',
          key: 'clientId',
          label: '客户账号',
          placeholder: '请输入客户账号搜索',
          options: [],
          // 远程查询函数：入参为关键字，返回 Promise<options[]>
          // SimpleTable 会对其做防抖处理（默认 300ms）
          queryCallback: query => {
            return searchCustomerOptions({keywords: query}).then(res => res.data.payload || []);
          }
        },
        {
          // 请求参数：salesmanId
          type: 'select',
          key: 'salesmanId',
          label: '销售账号',
          placeholder: '请选择销售账号',
          options: [],
          // 支持输入过滤 + 可清空
          filterable: true,
          clearable: true
        },
        {
          // 请求参数：serviceType
          type: 'select',
          key: 'serviceType',
          label: '送仓类型',
          placeholder: '请选择送仓类型',
          options: [],
          filterable: true,
          clearable: true
        },
        {
          // 请求参数：deliveryWarehouse
          type: 'select',
          key: 'deliveryWarehouse',
          label: '送仓仓库',
          placeholder: '请选择送仓仓库',
          options: [],
          filterable: true,
          clearable: true
        },
        {
          // 请求参数：serviceWarehouse
          type: 'select',
          key: 'serviceWarehouse',
          label: '服务仓库',
          placeholder: '请选择服务仓库',
          options: [],
          filterable: true,
          clearable: true
        },
        {
          // 请求参数：withTrash
          type: 'select',
          key: 'withTrash',
          label: '查看类型',
          placeholder: '请选择查看类型',
          options: [
            { label: '全部', value: 'ALL' },
            { label: '已删除', value: 'DELETED' },
            { label: '正常记录', value: 'NORMAL' },
          ],
          default: 'NORMAL'
        }
      ],

      // ============ 下拉数据填充 ============
      // 一次请求拿到全部下拉数据，再按 key -> options 映射一次性分发。
      // 调用方：SimpleTable.mounted 中 await 后写回各 header.options。
      fullfilled: () => {
        // 送仓仓库 / 服务仓库 / 销售账号同源一个接口，用三个字段区分，
        // 因此只在这一次请求里取回，不做三次请求。
        return getExtraFeesOptions().then(res => {
          const payload = res.data.payload || {};
          // 返回结构：{ targetWarehouse: [{value,label}], serviceWarehouse: [...], salesman: [...] }
          return {
            deliveryWarehouse: payload.targetWarehouse || [],
            serviceWarehouse: payload.serviceWarehouse || [],
            salesmanId: payload.salesman || [],
            serviceType: payload.serviceType || []
          };
        }).catch(() => ({
          deliveryWarehouse: [],
          serviceWarehouse: [],
          salesmanId: [],
          serviceType: []
        }));
      },

      // ============ 操作按钮 ============
      buttons: [
        // 单个按钮：创建杂费
        {
          key: 'create',
          label: '创建仓租',
          type: 'primary',
          icon: 'md-add',
          action: 'create'
        },
        // 下拉按钮：导出清单
        {
          key: 'export',
          label: '导出清单',
          type: 'default',
          icon: 'md-download',
          // 使用 exceljs 导出 xlsx（支持单元格边框）：exportAll 导出全部，exportSelected 仅导出勾选行
          list: [
            {key: 'exportAll', label: '导出全部数据', action: 'exportAll'},
            {key: 'exportSelected', label: '导出选中数据', action: 'exportSelected'}
          ]
        }
      ],

      // ============ 模态框配置 ============
      // 仅配置大小 + 打开回调，内部组件接收 params / close / setTitle
      modals: {
        create: {
          width: 520,
          height: 400,
          // 异步组件，避免无用依赖被打进首屏
          component: () => import("@/view/order/extraFees/modal/CreateFeeModal.vue"),
          open({ setParams, close, setTitle }) {
            setTitle("创建杂费");
            // 需要预置参数时在这里 setParams(...)，只放业务数据
            setParams({ category: "storageFees" });
            // 拿到 close 可按需延迟关闭；这里交给内部组件触发
            // （close / refresh 都由 SimpleTable 直接透传为 prop，不用放 params）
            void close;
          }
        },
        // 日志弹窗：由操作列「日志」按钮触发（vm.openModal('log', { row, index, category })）
        log: {
          width: 920,
          height: 560,
          component: () => import("@/view/priceRule/incidentalCharge/modal/FeeLogger.vue"),
          // payload 即 openModal 的第 2 个参数
          open({ setParams, close, setTitle }, payload = {}) {
            // 读取记录的日志
            getFeeLogger({id: payload.row.id}).then(res => {
              setTitle(`${payload.row.order_number}杂费操作记录日志`);
              setParams({
                recordLogger: res.data.payload
              })
              // 拿到 close 可按需延迟关闭；这里交给内部组件触发
              void close;
            })
          }
        },
      }
    };
  }

  if (category === 'waitCharge' || category === 'alreadyCharge') {
    return {
      columns: [
        {type: 'index', title: '序号', width: 70, align: 'center', fixed: 'left'},
        {title: '订单号', key: 'order_number', width: 150, align: 'center', tooltip: true, fixed: 'left'},
        {title: '客户账号', key: 'customer_account', width: 160, align: 'center', tooltip: true, fixed: 'left'},
        {title: '客户公司名称', key: 'customer_company_name', width: 260, align: 'center', tooltip: true},
        {title: '销售名称', key: 'sales_name', width: 180, align: 'center', tooltip: true},
        {title: '账单编号', key: 'bill_number', width: 150, align: 'center', tooltip: true},
        {title: '账单状态', key: 'bill_status', width: 140, align: 'center', tooltip: true},
        {title: '报价状态', key: 'quote_status', width: 110, align: 'center', tooltip: true},
        {title: '报价时间', key: 'quote_time', width: 180, align: 'center', tooltip: true},
        {title: '报价描述', key: 'quote_desc', width: 200, align: 'center', tooltip: true},
        {title: '收费科目', key: 'expense_item', width: 160, align: 'center', tooltip: true},
        {title: '收费单价', key: 'charge_price', width: 110, align: 'center', tooltip: true},
        {title: '收费单位', key: 'charge_unit', width: 100, align: 'center', tooltip: true},
        {title: '收费数量', key: 'charge_quantity', width: 100, align: 'center', tooltip: true},
        {title: '收费金额', key: 'charge_amount', width: 110, align: 'center', tooltip: true},
        {title: '币种', key: 'charge_currency', width: 110, align: 'center', tooltip: true},
        {title: '来源', key: 'source', width: 120, align: 'center', tooltip: true},
        {
          // 操作列：waitCharge -> 开始报价，其余（alreadyCharge）-> 查看报价
          // 点击统一走 modals.createOrView，并把当前行传给 open 回调
          title: '操作',
          key: 'action',
          width: 120,
          align: 'center',
          fixed: 'right',
          render: (h, params) => {
            return h('Button', {
              props: {type: 'primary', size: 'small'},
              on: {
                click: () => {
                  if (vm && vm.openModal) {
                    vm.openModal('createOrView', {
                      row: params.row,
                      index: params.index,
                      category
                    });
                  }
                }
              }
            }, category === 'waitCharge' ? '开始报价' : '查看报价');
          }
        },

      ],
      headers: [
        {
          type: 'inputFuzzy',
          key: 'shipmentNumber',
          label: '订单号',
          placeholder: '请输入订单号',
          // 多行文本域：开启后一次可输入多个订单号（每行一个）
          textarea: true,
          rows: 1
        },
        {
          // 请求参数：clientId = 选中的客户 id
          // 选项默认按 { value, label } 取值；如字段不同可传 config: { label, value }
          type: 'autocomplete',
          key: 'clientId',
          label: '客户账号',
          placeholder: '请输入客户账号搜索',
          options: [],
          // 远程查询函数：入参为关键字，返回 Promise<options[]>
          // SimpleTable 会对其做防抖处理（默认 300ms）
          queryCallback: query => {
            return searchCustomerOptions({keywords: query}).then(res => res.data.payload || []);
          }
        },
        {
          // 请求参数：expenseType = 选中的费用值
          // 选项由 fullfilled 从后端取回，结构为 [{ value, label }]
          type: 'select',
          key: 'expenseType',
          label: '费用',
          placeholder: '请选择费用',
          options: [],
          filterable: true,
          clearable: true
        },
      ],
      // ============ 下拉数据填充 ============
      // 返回 { [header.key]: options } 映射，由 SimpleTable 写回各 header.options
      fullfilled: () => {
        return getFeeOptions()
          .then(res => ({
            expenseType: res.data.payload || []
          }))
          .catch(() => ({
            expenseType: []
          }));
      },
      buttons: [],
      // ============ 模态框配置 ============
      modals: {
        // 开始报价 / 查看报价：由操作列按钮触发
        // 占位：业务逻辑（拉取报价详情、提交报价等）后续在内部组件里实现
        createOrView: {
          width: 920,
          height: 560,
          component: () => import("@/view/priceRule/incidentalCharge/modal/AssignChargeFee.vue"),
          // payload 为 openModal 的第二个参数，这里拿到操作列的 { row, index, category }
          open({ setParams, close, setTitle }, payload = {}) {
            setTitle(category === 'waitCharge' ? '开始报价' : '查看报价');
            const row = payload.row || {};
            // 基础参数：把当前行的 id 一并带上（弹窗提交时要回传），
            // row 也留着，方便后续需要整行数据时取用
            const base = { category, id: row.id, row, rowIndex: payload.index };
            // 先在 setParams 之前调用明细接口，拿到结果再注入；
            // 弹窗组件是响应式的，晚到也会正确回显
            buildChargeFeeParams(row)
              .then(detail => setParams(Object.assign({}, base, detail)))
              // 后端出错只提示，不注入兜底数据
              .catch(err => {
                vm.$Message.error((err && err.message) || "获取报价明细失败");
              });
            // 拿到 close 可按需延迟关闭；这里交给内部组件触发
            void close;
          }
        }
      }
    }
  }
}
